import { useCallback, useMemo } from 'react';

import { ValidationItems } from '@kanda-libs/ks-component-ts';

export interface DateOfBirthValidationHook {
  validation: ValidationItems;
}

export default function useDateOfBirthValidation(): DateOfBirthValidationHook {
  const parseDate = useCallback((date: string) => {
    const formatted = date?.replace(/ /g, '')?.split('/');
    if (formatted?.length !== 3) return null;
    const reordered = [formatted[1], formatted[0], formatted[2]]?.join('/');
    const parsed = new Date(reordered);
    if (
      (!Object.prototype.toString.call(parsed) as unknown as string) ===
      '[object Date]'
    )
      return null;
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed as Date;
  }, []);

  const validateDate = useCallback(
    (inputDate?: boolean | string | number) => {
      if (typeof inputDate !== 'string') return false;
      const date = parseDate(inputDate);
      return !!date;
    },
    [parseDate],
  );

  const testOver18 = useCallback(
    (inputDate?: boolean | string | number) => {
      if (typeof inputDate !== 'string') return false;
      const date = parseDate(inputDate);
      const today = new Date();
      if (!date) {
        return false;
      }
      const age = today.getFullYear() - date.getFullYear();
      return age >= 18;
    },
    [parseDate],
  );

  const testUnder100 = useCallback(
    (inputDate?: boolean | string | number) => {
      if (typeof inputDate !== 'string') return false;
      const date = parseDate(inputDate);
      const today = new Date();
      if (!date) {
        return false;
      }
      const age = today.getFullYear() - date.getFullYear();
      return age <= 100;
    },
    [parseDate],
  );

  const validation: ValidationItems = useMemo(
    () => ({
      validate: {
        value: {
          valid: (value?: boolean | string | number) => validateDate(value),
          moreThan18: (value?: boolean | string | number) => testOver18(value),
          lessThan100: (value?: boolean | string | number) =>
            testUnder100(value),
        },
        message: {
          valid: 'You must enter a valid date',
          moreThan18: 'You must be over 18 years of age',
          lessThan100: 'You must be under 100 years of age',
        },
      },
    }),
    [validateDate, testOver18, testUnder100],
  );

  return {
    validation,
  };
}
